/**
 * Signed-in home (M1): accounts, the sync status line, and the inbox read from the
 * local DB. Nothing here waits on the network for synced data.
 */
import { actions, inverseDelta, type LabelDelta } from "@homing/core";
import { jsonArray } from "@homing/core/client";
import { Kbd, MessageRow, StatusLine, ThreadMessage } from "@homing/ui-web";
import { useCallback, useEffect, useRef, useState } from "react";
import { mailDb, type MailDb } from "../db/client.ts";
import { useDbState, useQuery } from "../db/hooks.ts";
import { connectGoogle, useAccounts, useSelectedAccount } from "../lib/accounts.ts";
import { longWhen, shortWhen, statusItems } from "../lib/format.ts";
import { logout, type Me } from "../lib/session.ts";

export function Mailbox({ me, offline }: { me: Me; offline: boolean }) {
  const { accounts } = useAccounts(me);
  const [selected, select] = useSelectedAccount(accounts);
  const account = accounts?.find((a) => a.id === selected) ?? null;
  return (
    <div className="chrome" style={{ minHeight: "100%", display: "flex", flexDirection: "column" }}>
      <header className="topbar">
        <span className="eyebrow">Homing</span>
        <nav aria-label="Accounts" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {accounts?.map((a) => (
            <button key={a.id} className="btn" aria-pressed={a.id === selected} onClick={() => select(a.id)} data-testid="account">
              {a.email}
              {a.status !== "active" ? <span className="muted"> · {a.status.replace("_", " ")}</span> : null}
            </button>
          ))}
          <button className="btn" onClick={connectGoogle} disabled={offline} data-testid="connect-google">
            Connect Google
          </button>
        </nav>
        <span className="spacer" />
        <span className="m muted" data-testid="me-email">{me.email}</span>
        <button className="btn" onClick={() => void logout()} data-testid="logout">
          Sign out
        </button>
      </header>
      {account ? (
        <MailboxView key={account.id} db={mailDb()} accountId={account.id} me={me} />
      ) : accounts ? (
        <section className="thread">
          <p className="empty" style={{ fontSize: 21 }} data-testid="no-accounts">
            No mailbox is connected yet. Connect a Google account and your mail will start arriving here, newest first.
          </p>
        </section>
      ) : null}
    </div>
  );
}

interface ThreadRow {
  [k: string]: string | number | null;
  id: string;
  subject: string;
  snippet: string;
  last_message_at: number;
  message_count: number;
  unread_count: number;
  label_ids: string;
  participants: string;
  starred: number;
}

const INBOX_SQL =
  "SELECT id, subject, snippet, last_message_at, message_count, unread_count, label_ids, participants, starred FROM threads WHERE in_inbox = 1 ORDER BY last_message_at DESC LIMIT 500";

interface Undo {
  threadIds: string[];
  inverse: LabelDelta;
  label: string;
}

function who(participants: string, me: string): string {
  try {
    const ps = (JSON.parse(participants) as { name?: string; email?: string }[]).filter((p) => p.email?.toLowerCase() !== me.toLowerCase());
    const display = ps.map((p) => (p.name || p.email?.split("@")[0] || "").replace(/^["']|["']$/g, "").trim()).filter(Boolean);
    const unique = [...new Set(display)];
    if (!unique.length) return "me";
    // One sender: their full display name ("The Kernel Letter", "Élodie Marchand").
    // A conversation: first names, so several fit ("Chris, Ravi, Élodie").
    return unique.length === 1 ? unique[0]! : unique.map((n) => n.split(/\s+/)[0]).join(", ");
  } catch {
    return "";
  }
}

function MailboxView({ db, accountId, me }: { db: MailDb; accountId: string; me: Me }) {
  const state = useDbState(db);
  useEffect(() => {
    void db.open(accountId);
  }, [db, accountId]);
  const { rows } = useQuery<ThreadRow>(db, INBOX_SQL, [], ["threads"]);
  const [cursor, setCursor] = useState<string | null>(null);
  const lastIndex = useRef(0);
  const [open, setOpen] = useState<string | null>(null);
  const undo = useRef<Undo[]>([]);
  const [toast, setToast] = useState<{ text: string; undoable: boolean } | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep the cursor on a real row: when the selected thread leaves the inbox, take
  // whatever now sits where it was.
  const index = rows ? rows.findIndex((r) => r.id === cursor) : -1;
  // Set by undo: the thread is coming back, so hold the cursor on it until it does.
  const awaiting = useRef<string | null>(null);
  useEffect(() => {
    if (!rows) return;
    if (index >= 0) {
      lastIndex.current = index;
      if (awaiting.current === cursor) awaiting.current = null;
      return;
    }
    if (cursor && awaiting.current === cursor) return;
    const next = rows[Math.min(lastIndex.current, rows.length - 1)];
    setCursor(next ? next.id : null);
  }, [rows, index, cursor]);

  const flash = useCallback((text: string, undoable = false) => {
    setToast({ text, undoable });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 6000);
  }, []);

  useEffect(() => db.onRejected((e) => flash(`The server refused that change (${e}). It was undone.`)), [db, flash]);

  function act(row: ThreadRow, delta: LabelDelta, label: string) {
    const before = jsonArray(row.label_ids);
    undo.current.push({ threadIds: [row.id], inverse: inverseDelta(before, delta), label });
    void db.api.mutate("thread.modifyLabels", { threadIds: [row.id], add: [...delta.add], remove: [...delta.remove] });
    flash(label, true);
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.isComposing) return;
      if (e.target instanceof HTMLElement && e.target.closest("input, textarea, select, [contenteditable]")) return;
      const list = rows ?? [];
      const i = list.findIndex((r) => r.id === cursor);
      const row = i >= 0 ? list[i] : undefined;
      if (open) {
        if (e.key === "Escape") {
          e.preventDefault();
          setOpen(null);
        }
        return;
      }
      switch (e.key) {
        case "j":
          if (i < list.length - 1) setCursor(list[i + 1]!.id);
          break;
        case "k":
          if (i > 0) setCursor(list[i - 1]!.id);
          break;
        case "Enter":
          if (row) setOpen(row.id);
          break;
        case "e":
          if (row) act(row, actions.done, "Marked done");
          break;
        case "I":
          if (row) act(row, actions.markRead, "Marked read");
          break;
        case "s":
          if (row) act(row, row.starred ? actions.unstar : actions.star, row.starred ? "Unstarred" : "Starred");
          break;
        case "u": {
          const last = undo.current.pop();
          if (!last) return;
          void db.api.mutate("thread.modifyLabels", { threadIds: last.threadIds, add: [...last.inverse.add], remove: [...last.inverse.remove] });
          awaiting.current = last.threadIds[0]!;
          setCursor(last.threadIds[0]!);
          flash(`Undid: ${last.label.toLowerCase()}`);
          break;
        }
        default:
          return;
      }
      e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  useEffect(() => {
    if (cursor) document.querySelector(`[data-id="${CSS.escape(cursor)}"]`)?.scrollIntoView({ block: "nearest" });
  }, [cursor]);

  return (
    <main style={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <StatusLine testId="sync-status" items={statusItems(state.sync, { blocked: state.blocked, error: state.error })} />
      {open ? (
        <ThreadView db={db} threadId={open} onClose={() => setOpen(null)} />
      ) : rows && rows.length === 0 ? (
        <section className="thread">
          <p className="empty" style={{ fontSize: 21 }} data-testid="inbox-empty">
            Nothing left. The inbox is empty.
          </p>
        </section>
      ) : (
        <div role="listbox" aria-label="Inbox" className="list" data-testid="inbox">
          {rows?.map((r) => (
            <MessageRow
              key={r.id}
              rowId={r.id}
              who={who(r.participants, me.email)}
              subject={r.subject || "(no subject)"}
              snippet={r.snippet}
              when={shortWhen(r.last_message_at)}
              unread={r.unread_count > 0}
              starred={!!r.starred}
              count={r.message_count}
              selected={r.id === cursor}
              onClick={() => setCursor(r.id)}
              onDoubleClick={() => setOpen(r.id)}
            />
          ))}
        </div>
      )}
      {toast ? (
        <div style={{ position: "fixed", left: "var(--gutter)", bottom: 24 }}>
          <span className="toast" role="status" data-testid="toast">
            <span>{toast.text}</span>
            {toast.undoable ? (
              <span className="m muted">
                undo <Kbd keys="u" />
              </span>
            ) : null}
          </span>
        </div>
      ) : null}
    </main>
  );
}

interface MessageRowData {
  [k: string]: string | number | null;
  id: string;
  from_name: string;
  from_email: string;
  snippet: string;
  internal_date: number;
}

function ThreadView({ db, threadId, onClose }: { db: MailDb; threadId: string; onClose: () => void }) {
  const thread = useQuery<{ subject: string }>(db, "SELECT subject FROM threads WHERE id = ?", [threadId], ["threads"]).rows?.[0];
  const { rows } = useQuery<MessageRowData>(
    db,
    "SELECT id, from_name, from_email, snippet, internal_date FROM messages WHERE thread_id = ? ORDER BY internal_date",
    [threadId],
    ["messages"],
  );
  const [bodies, setBodies] = useState<Record<string, { text: string | null; state: "loading" | "ready" | "error"; note?: string }>>({});
  const ids = rows?.map((r) => r.id).join(",") ?? "";
  useEffect(() => {
    const ac = new AbortController();
    for (const id of ids ? ids.split(",") : []) {
      db.api.body(id).then(
        (body) =>
          !ac.signal.aborted &&
          setBodies((b) => ({
            ...b,
            [id]: body.text !== null ? { text: body.text, state: "ready" } : { text: null, state: "error", note: "This message has no plain-text part. HTML mail arrives with M2." },
          })),
        () =>
          !ac.signal.aborted &&
          setBodies((b) => ({
            ...b,
            [id]: { text: null, state: "error", note: navigator.onLine ? "The message couldn't be loaded." : "The full message isn't available offline." },
          })),
      );
    }
    return () => ac.abort();
  }, [db, ids]);
  return (
    <section className="thread" data-testid="thread">
      <button className="btn" onClick={onClose}>
        Back <Kbd keys="esc" />
      </button>
      <h1>{thread?.subject || "(no subject)"}</h1>
      {rows?.map((m) => {
        const b = bodies[m.id];
        return (
          <ThreadMessage
            key={m.id}
            from={m.from_name || m.from_email}
            email={m.from_email}
            when={longWhen(m.internal_date)}
            snippet={m.snippet}
            body={b?.text ?? null}
            state={b?.state ?? "loading"}
            {...(b?.note ? { note: b.note } : {})}
          />
        );
      })}
    </section>
  );
}
